import { Button, Css, ModalBody, ModalFooter, ModalHeader, Palette, useModal } from "@homebound/beam";
import { useId, useState } from "react";
import { PRODUCT_FALLBACK_IMG_URL } from "src/utils/constants";
import { MergeMaterialVariants_MaterialVariantFragment } from "./MergeMaterialVariants.generated";

type FeaturedImagePickerModalProps = {
  variants: MergeMaterialVariants_MaterialVariantFragment[];
  selectedImageId?: string;
  onSelect: (imageId: string) => void;
};

export function FeaturedImagePickerModal(props: FeaturedImagePickerModalProps) {
  const { variants, selectedImageId, onSelect } = props;
  const { closeModal } = useModal();
  const groupName = useId();
  const images = variants.flatMap((mv) => mv.images.map((image) => ({ ...image, variantName: mv.displayName })));
  const [pendingId, setPendingId] = useState<string | undefined>(selectedImageId);

  return (
    <>
      <ModalHeader>Select an Image</ModalHeader>
      <ModalBody>
        {images.isEmpty ? (
          <div css={Css.gray700.$}>No images available on the selected materials.</div>
        ) : (
          <>
            <div css={Css.sm.gray700.mb2.$}>Select an image to use for the merged material.</div>
            <div css={Css.df.fww.gap3.$} data-testid="featuredImageGrid" role="radiogroup" aria-label="Featured image">
              {images.map((image) => (
                <FeaturedImageOption
                  key={image.id}
                  name={groupName}
                  value={image.id}
                  label={image.variantName}
                  imageUrl={image.asset?.previewUrl || image.asset?.downloadUrl || PRODUCT_FALLBACK_IMG_URL}
                  selected={image.id === pendingId}
                  onSelect={setPendingId}
                />
              ))}
            </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="quaternary" label="Cancel" onClick={closeModal} />
        <Button
          label="Select"
          disabled={!pendingId}
          onClick={() => {
            if (!pendingId) return;
            onSelect(pendingId);
            closeModal();
          }}
        />
      </ModalFooter>
    </>
  );
}

type FeaturedImageOptionProps = {
  name: string;
  value: string;
  label: string;
  imageUrl: string;
  selected: boolean;
  onSelect: (value: string) => void;
};

/** Radio + 120×120 image paired as a single selectable unit.
 * This is a temporary component until we add something like this to Beam
 */
function FeaturedImageOption(props: FeaturedImageOptionProps) {
  const { name, value, label, imageUrl, selected, onSelect } = props;
  return (
    <label css={Css.df.aic.gap1.cursorPointer.$} data-testid="featuredImageOption">
      <input
        type="radio"
        name={name}
        value={value}
        checked={selected}
        aria-label={label}
        onChange={() => onSelect(value)}
        css={Css.sqPx(16).fs0.cursorPointer.add("accentColor", Palette.Blue700).$}
      />
      <div css={Css.sqPx(120).ba.bw1.bcGray200.br8.bgWhite.df.aic.jcc.oh.p1.fs0.$}>
        <img css={Css.w100.h100.objectContain.$} src={imageUrl} alt="" />
      </div>
    </label>
  );
}
